import { type FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageComponent } from '@/components/Image';
import { replaceFsCount, replaceTermsTillDate } from '@/modules/promo';
import { useValidDate } from '@/modules/promo/hooks/useValidDateFromQuery';
import type { HeroCardBenefit } from '../..';
import { ScHeroCard } from './styled';

interface IHeroCard {
  heroImage: string;
  translation: string;
  isActive: boolean;
  cardBenefits?: HeroCardBenefit[];
  validDate?: string;
  fsCount?: number | null;
}

export const HeroCard: FC<IHeroCard> = memo(
  ({ heroImage, translation, isActive, cardBenefits, fsCount, validDate }) => {
    const { t } = useTranslation([translation, 'glossary']);
    const [flipped, setFlipped] = useState(false);

    const validDateFromQuery = useValidDate(validDate);

    useEffect(() => {
      if (isActive && flipped) return;

      setFlipped(false);
    }, [isActive, flipped]);

    const initialBenefits: HeroCardBenefit[] = useMemo(
      () => [
        {
          image: '/lp/dragon-pearls-november-25/fire.webp',
          translationKey: 'heroCard.wager',
        },
        {
          image: '/lp/dragon-pearls-november-25/headphones.webp',
          translationKey: 'heroCard.support',
        },
      ],
      []
    );

    const benefits = useMemo(
      () => cardBenefits || initialBenefits,
      [initialBenefits, cardBenefits]
    );

    return (
      <ScHeroCard>
        <div className={`card ${flipped ? 'flipped' : ''}`}>
          <div className="front">
            <ImageComponent
              alt="hero"
              className="hero-img"
              height={250}
              src={heroImage}
              width={446}
            />
            <div className="text">
              <p className="title">{t('heroCard.title')}</p>
              <p className="fs-count">
                {replaceFsCount(t('heroCard.prizeFs'), fsCount)}
              </p>
              <p className="fs">{t('heroCard.fs')}</p>
            </div>
            <div className="benefits">
              {benefits.map((item) => (
                <div key={item.translationKey}>
                  <ImageComponent
                    alt={t(item.translationKey)}
                    height={40}
                    src={item.image}
                    width={40}
                  />
                  <p>{t(item.translationKey)}</p>
                </div>
              ))}
            </div>
            <p className="terms-btn" onClick={() => setFlipped(!flipped)}>
              <span>{t('common:button.termsAndConditions')}</span>
              <ImageComponent
                alt="arrowTerms"
                height={16}
                src="/lp/dragon-pearls-november-25/arrow-terms.svg"
                width={16}
              />
            </p>
          </div>
          <div className="back">
            <ul>
              {replaceTermsTillDate(
                Object.values(
                  t('heroCard.terms', {
                    returnObjects: true,
                    ...(validDateFromQuery ? { date: validDateFromQuery } : {}),
                  }) as Record<string, string>
                ),
                validDateFromQuery
              ).map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
            <p className="terms-btn" onClick={() => setFlipped(!flipped)}>
              <span>{t('auth.registration.buttons.back')}</span>
              <ImageComponent
                alt="arrowTerms"
                height={16}
                src="/lp/dragon-pearls-november-25/arrow-terms.svg"
                width={16}
              />
            </p>
          </div>
        </div>
      </ScHeroCard>
    );
  }
);
